// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xUpdateAccountSettings from "@steiner/App/Handlers/AccountSettings/UpdateAccountSettings";
import { CustomAppState, XAccountStateType } from "@steiner/App/Store/Reducers";
import { placeHolders } from "@steiner/XDL/constants";
import { xValidateOnSubmit } from "@steiner/XDL/Utilities";
import xMasks from "@steiner/XDL/Utilities/xValidate/xMasks";
import React, { ChangeEvent, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
    // xng-change
    xUpdateAccountSettings,
};

const mapStateToProps = (state: CustomAppState) => ({
    editingAccount: state.pages.accountSettings.editingAccount,
    emailErrorMessage: state.pages.accountSettings.emailErrorMessage,
    useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
    changePasswordUrl: getPageLinkByPageType(state, "ChangePasswordPage")?.url,
    // xng-change
    updatedAccount: state.xAccount,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsUserInformationStyles {
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    // xng-change
    changePasswordLinkWrapper?: InjectableCss;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    userInformationGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h2",
    },
};

const styles = userInformationStyles;
const password = "*************";

const formId = "AccountSettingsInfo";

const AccountSettingsUserInformation = ({
    editingAccount,
    useEmailAsUserName,
    changePasswordUrl,
    emailErrorMessage,
    updateAccountSettings,
    // xng-change
    xUpdateAccountSettings,
    updatedAccount,
}: Props) => {
    if (!editingAccount) {
        return null;
    }

    const fullName = `${editingAccount.firstName} ${editingAccount.lastName}`;
    const emailChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        updateAccountSettings({
            email: event.currentTarget.value,
        });
    };

    // <XNG-Changes>
    const customFieldChangeHandler = (event: ChangeEvent<HTMLInputElement>, fieldName: keyof XAccountStateType) => {
        xUpdateAccountSettings({
            [`${fieldName}`]: event.currentTarget.value,
        });
    };

    useEffect(() => {
        const { firstName, lastName, properties } = editingAccount;

        xUpdateAccountSettings({
            firstName,
            lastName,
            company: updatedAccount?.company || properties?.company,
            jobTitle: updatedAccount?.jobTitle || properties?.jobTitle,
            phone: updatedAccount?.phone || properties?.phone,
        });
    }, [editingAccount]);

    useEffect(() => {
        const errors = xValidateOnSubmit(formId);

        xUpdateAccountSettings({
            isCustomFieldsValid: !errors?.length,
        });
    }, [updatedAccount]);
    // </XNG-Changes>

    const label = (
        <StyledWrapper {...styles.changePasswordLinkWrapper}>
            <Typography>{translate("Password")}</Typography>
            <Link {...styles.changePasswordLink} href={changePasswordUrl}>
                {/* xng-change */}
                {translate("Edit")}
            </Link>
        </StyledWrapper>
    );

    return (
        <>
            <Typography {...styles.userInformationTitle}>{translate("Your Information")}</Typography>
            <GridContainer>
                {!useEmailAsUserName && (
                    <GridItem {...styles.userInformationGridItem}>
                        <TextField
                            label={translate("User Name")}
                            value={editingAccount.userName}
                            data-test-selector="accountSettings_userName"
                            disabled
                        />
                    </GridItem>
                )}
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="email"
                        label={translate(!useEmailAsUserName ? "Email" : "Email (User Name)")}
                        onChange={emailChangeHandler}
                        value={editingAccount.email}
                        data-test-selector="accountSettings_email"
                        required
                        maxLength={256}
                        error={emailErrorMessage}
                    />
                </GridItem>
                {/* <GridItem {...styles.userInformationGridItem}>
                    <TextField label={translate("Full Name")} value={fullName} disabled />
                </GridItem> */}
                <GridItem {...styles.userInformationGridItem}>
                    <TextField type="password" label={label} disabled value={password} />
                </GridItem>
                {/* <xng-change> */}
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="text"
                        label={translate("First Name")}
                        onChange={e => customFieldChangeHandler(e, "firstName")}
                        value={updatedAccount?.firstName}
                        data-test-selector="accountSettings_firstName"
                        required
                        maxLength={75}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="text"
                        label={translate("Last Name")}
                        onChange={e => customFieldChangeHandler(e, "lastName")}
                        value={updatedAccount?.lastName}
                        data-test-selector="accountSettings_lastName"
                        required
                        maxLength={75}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="text"
                        label={translate("Company")}
                        onChange={e => customFieldChangeHandler(e, "company")}
                        value={updatedAccount?.company}
                        data-test-selector="accountSettings_company"
                        required
                        maxLength={75}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="text"
                        label={translate("Job title")}
                        onChange={e => customFieldChangeHandler(e, "jobTitle")}
                        value={updatedAccount?.jobTitle}
                        data-test-selector="accountSettings_jobTitle"
                        required
                        maxLength={25}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="phone"
                        label={translate("Phone")}
                        onChange={e => customFieldChangeHandler(e, "phone")}
                        value={xMasks.phone.transform(updatedAccount.phone || "", "")}
                        data-test-selector="accountSettings_phone"
                        required
                        maxLength={25}
                        placeholder={placeHolders.phone}
                        {...{ formId }}
                    />
                </GridItem>
                {/* </xng-change> */}
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsUserInformation),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "User Information",
    },
};

export default widgetModule;
